"use client"

import { useState, type ReactNode } from "react"
import { Check, Loader2, Mail, Plus, Search } from "lucide-react"

import { avatarHex, stageTint, TINT } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import type { BucketRow, CompanyRow } from "@/hooks/use-community"

/**
 * Companies and Buckets, from canvas artboards 17a and 17e.
 *
 * Both were card grids. Turn 17's framing is that the question on these screens
 * "is never who is in here but who has gone quiet", and a grid of cards fits
 * nine records while hiding the numbers that answer that — so both are
 * zebra-striped tables now, and the figures lead.
 *
 * Purely presentational; everything arrives from `useCommunity`.
 */

const HEAD =
  "text-[10px] font-normal uppercase tracking-[0.1em] text-muted-foreground"

/** Zebra, at the weight the canvas draws: a tint off the foreground, not a fill. */
const ZEBRA = "odd:bg-foreground/[0.025]"

export function CommunityToolbar({
  placeholder,
  value,
  onChange,
  action,
  onAction,
  children,
}: {
  placeholder: string
  value: string
  onChange: (next: string) => void
  action: string
  onAction: () => void
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-none flex-wrap items-center gap-4 pb-1 pt-3.5">
      <span className="inline-flex min-w-0 flex-1 items-center gap-2 border-b pb-1.5">
        <Search className="size-3.5 flex-none text-muted-foreground" />
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground"
        />
      </span>
      {children}
      <button
        type="button"
        onClick={onAction}
        className="inline-flex h-9 flex-none items-center gap-2 whitespace-nowrap rounded-md bg-primary px-[15px] text-[12.5px] font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        <Plus className="size-[13px]" />
        {action}
      </button>
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-6 text-[12.5px] text-muted-foreground">{children}</p>
}

function Loading() {
  return (
    <div className="flex items-center gap-2 py-4 text-[12.5px] text-muted-foreground">
      <Loader2 className="size-3.5 animate-spin" />
      Loading…
    </div>
  )
}

/**
 * A wide table in a narrow pane.
 *
 * 17a, 17c and 17e each give their columns a width and several of those are
 * fixed, so under a narrow pane the `fr` columns are what gives. At 390 the
 * buckets table had 314px of fixed columns and 70px of gaps inside a 390px
 * window, which left the bucket's *name* at nothing: rows carried a status, a
 * count and an interval belonging to a bucket nobody could identify, under two
 * column headers printed on top of each other.
 *
 * These are reference tables — six and seven columns meant to be read across —
 * so every column keeps the width it was drawn at and the table scrolls
 * sideways instead. That is deliberately a different answer from the one
 * Placements gets, where a row is three facts about one person and stacking
 * reads better than swiping.
 */
function WideTable({
  minWidth,
  children,
}: {
  /** The narrow case's floor, as a Tailwind class — e.g. `min-w-[620px]`. */
  minWidth: string
  children: ReactNode
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-auto">
      <div className={cn("flex min-h-0 flex-1 flex-col @2xl:min-w-0", minWidth)}>
        {children}
      </div>
    </div>
  )
}

const COMPANY_GRID =
  "grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.6fr)_74px_54px_92px] items-baseline gap-3.5"

export function CompaniesTable({
  rows,
  isLoading,
  onOpen,
}: {
  rows: CompanyRow[]
  isLoading: boolean
  onOpen: (id: string) => void
}) {
  return (
    <WideTable minWidth="min-w-[620px]">
      <div className={cn(COMPANY_GRID, "flex-none pb-2.5 pt-4")}>
        <span className={HEAD}>Company</span>
        <span className={HEAD}>Location &amp; size</span>
        <span className={cn(HEAD, "text-right")}>Contacts</span>
        <span className={cn(HEAD, "text-right")}>Jobs</span>
        {/*
          17a's last column is "Last touch". Nothing records a touch against a
          company — touchpoints hang off a contact and a bucket — so the column
          says what it actually shows.
        */}
        <span className={cn(HEAD, "text-right")}>Added</span>
      </div>

      {/*
        Loading, empty and rows are mutually exclusive. They were not: a company
        list resolves before its counts do, so the spinner sat above rows all
        reading "0 contacts, 0 jobs" — which is not a slower answer, it is a
        wrong one.
      */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>No companies match this search.</Empty>
        ) : (
          rows.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => onOpen(row.id)}
            className={cn(
              COMPANY_GRID,
              ZEBRA,
              "w-full border-t px-3 py-3.5 text-left transition-colors hover:bg-accent",
            )}
          >
            <span className="min-w-0">
              <span className="block truncate text-[12.5px] font-medium">{row.name}</span>
              {row.industry && (
                <span className="mt-1.5 block truncate text-[11px] text-muted-foreground">
                  {row.industry}
                </span>
              )}
            </span>

            <span className="min-w-0">
              <span className="block truncate text-[12px] text-muted-foreground">
                {row.where ?? "—"}
              </span>
              {row.website && (
                <span className="mt-1.5 block truncate font-mono text-[11px] text-muted-foreground">
                  {row.website}
                </span>
              )}
            </span>

            <span className="text-right font-mono text-[12px]">{row.contacts}</span>
            <span className="text-right font-mono text-[12px]">{row.jobs}</span>
            <span className="text-right font-mono text-[10.5px] text-muted-foreground">
              {row.added ?? "—"}
            </span>
          </button>
          ))
        )}
      </div>
    </WideTable>
  )
}

const BUCKET_GRID =
  "grid grid-cols-[minmax(0,1.6fr)_74px_96px_minmax(0,1.2fr)_88px_56px] items-start gap-3.5"

export function BucketsTable({
  rows,
  isLoading,
  onOpen,
  onToggleStatus,
}: {
  rows: BucketRow[]
  isLoading: boolean
  onOpen: (id: string) => void
  onToggleStatus: (row: BucketRow) => void
}) {
  return (
    <WideTable minWidth="min-w-[680px]">
      <div className={cn(BUCKET_GRID, "flex-none items-baseline pb-2.5 pt-4")}>
        <span className={HEAD}>Bucket</span>
        <span className={cn(HEAD, "text-right")}>Contacts</span>
        <span className={cn(HEAD, "text-right")}>Interval</span>
        <span className={HEAD}>Touch coverage</span>
        <span className={cn(HEAD, "text-right")}>Updated</span>
        <span />
      </div>

      {/* Same rule here: a bucket's size and its overdue count are two further
          queries, and a row drawn before they land says "—" and "all current",
          both of which read as answers. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>No buckets yet. One is a promise to stay in touch every so often.</Empty>
        ) : (
          rows.map((row) => (
          <div key={row.id} className={cn(BUCKET_GRID, ZEBRA, "border-t px-3 py-3.5")}>
            <button
              type="button"
              onClick={() => onOpen(row.id)}
              className="min-w-0 text-left"
            >
              <span className="flex items-baseline gap-2.5">
                <span className="min-w-0 truncate text-[12.5px] font-medium">
                  {row.name}
                </span>
                <span
                  className={cn(
                    "whitespace-nowrap font-mono text-[10px]",
                    row.status === "active" ? "text-brand-ink" : "text-muted-foreground",
                  )}
                >
                  {row.status}
                </span>
              </span>
              {row.description && (
                <span className="mt-1.5 block truncate text-[11px] text-muted-foreground">
                  {row.description}
                </span>
              )}
            </button>

            <span className="pt-0.5 text-right font-mono text-[12px]">
              {row.contacts ?? "—"}
            </span>
            <span className="pt-0.5 text-right font-mono text-[10.5px] text-muted-foreground">
              {row.interval ?? "—"}
            </span>

            {/*
              A bucket is a promise to stay in touch every N days, so the row
              says whether the promise is kept. The bar is the share of the
              bucket that is overdue; amber is the canvas's colour for
              "needs you", not an error.
            */}
            <div className="min-w-0 pt-0.5">
              <div
                className={cn(
                  "font-mono text-[10.5px]",
                  row.pastDue > 0 ? "text-attention" : "text-muted-foreground",
                )}
              >
                {row.pastDue > 0 ? `${row.pastDue} past due` : "all current"}
              </div>
              <div className="mt-2 h-[5px] w-full bg-accent">
                <div
                  className={cn("h-full", row.pastDue > 0 ? "bg-attention" : "bg-brand-ink")}
                  style={{
                    width: `${Math.max(2, Math.round((row.pastDue > 0 ? row.pastDueShare : 1) * 100))}%`,
                  }}
                />
              </div>
            </div>

            <span className="pt-0.5 text-right font-mono text-[10.5px] text-muted-foreground">
              {row.updated ?? "—"}
            </span>

            <button
              type="button"
              onClick={() => onToggleStatus(row)}
              className="pt-px text-right text-[12px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {row.status === "active" ? "Pause" : "Start"}
            </button>
          </div>
          ))
        )}
      </div>
    </WideTable>
  )
}

export type ContactRow = {
  id: string
  name: string
  initials: string
  company?: string
  companyId?: string
  title?: string
  email?: string
  type?: string
}

const CONTACT_GRID =
  "grid grid-cols-[14px_minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,1.1fr)_minmax(0,1.3fr)_66px_18px] items-center gap-3"

/**
 * Contacts, from artboard 17c: "Every column the source table carries, fitted
 * to the pane, plus the bucket-assignment bar the ContactSelector wraps it in."
 *
 * Name and company stay links, because that is what they already were.
 */
const PAGE_SIZE = 25

export function ContactsTable({
  rows,
  selectedIds,
  isLoading,
  onToggle,
  onOpenPerson,
  onOpenCompany,
  onEmail,
}: {
  rows: ContactRow[]
  selectedIds: string[]
  isLoading: boolean
  onToggle: (id: string) => void
  onOpenPerson: (id: string) => void
  onOpenCompany: (companyId: string) => void
  onEmail: (id: string) => void
}) {
  /*
    17c pages the table, and so did the component this replaced — a directory of
    1,284 people rendered in one list is a scroll nobody finishes. The endpoint
    returns them all, so the paging is local.
  */
  const [page, setPage] = useState(1)
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const current = Math.min(page, pages)
  const start = (current - 1) * PAGE_SIZE
  const shown = rows.slice(start, start + PAGE_SIZE)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={cn(CONTACT_GRID, "flex-none items-baseline pb-2.5 pt-4")}>
        <span />
        <span className={HEAD}>Name</span>
        <span className={HEAD}>Company</span>
        <span className={HEAD}>Title</span>
        <span className={HEAD}>Email</span>
        <span className={HEAD}>Type</span>
        <span />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>No contacts match these filters.</Empty>
        ) : (
          shown.map((row) => {
            const hex = avatarHex(row.id)
            return (
              <div key={row.id} className={cn(CONTACT_GRID, ZEBRA, "border-t px-3 py-3")}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(row.id)}
                  onChange={() => onToggle(row.id)}
                  aria-label={`Select ${row.name}`}
                  className="size-[13px] accent-[var(--primary)]"
                />

                <button
                  type="button"
                  onClick={() => onOpenPerson(row.id)}
                  className="flex min-w-0 items-center gap-2.5 text-left"
                >
                  <span
                    aria-hidden
                    className="flex size-6 flex-none items-center justify-center rounded-full text-[10px] font-medium"
                    style={{ color: hex, backgroundColor: stageTint(hex, TINT.pill) }}
                  >
                    {row.initials}
                  </span>
                  <span className="truncate text-[12.5px] font-medium text-brand-ink">
                    {row.name}
                  </span>
                </button>

                {row.companyId ? (
                  <button
                    type="button"
                    onClick={() => onOpenCompany(row.companyId as string)}
                    className="truncate text-left text-[12px] text-brand-ink"
                  >
                    {row.company ?? "—"}
                  </button>
                ) : (
                  <span className="truncate text-[12px] text-muted-foreground">
                    {row.company ?? "—"}
                  </span>
                )}

                <span className="truncate text-[12px]">{row.title ?? "—"}</span>
                <span className="truncate font-mono text-[10.5px] text-muted-foreground">
                  {row.email ?? "—"}
                </span>
                <span className="truncate font-mono text-[10.5px] text-muted-foreground">
                  {row.type ?? "—"}
                </span>

                {/* Not disabled without an address: the dialog lets you type
                    one, which is how a contact without an email gets reached. */}
                <button
                  type="button"
                  onClick={() => onEmail(row.id)}
                  aria-label={`Email ${row.name}`}
                  className="flex text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Mail className="size-[15px]" />
                </button>
              </div>
            )
          })
        )}
      </div>

      {rows.length > PAGE_SIZE && (
        <div className="flex flex-none items-center gap-4 border-t py-3">
          <span className="font-mono text-[11px] text-muted-foreground">
            Showing {start + 1}–{Math.min(start + PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <span className="ml-auto flex items-center gap-4">
            <button
              type="button"
              onClick={() => setPage(current - 1)}
              disabled={current === 1}
              className="text-[12px] text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <span className="font-mono text-[11px] text-muted-foreground">
              Page {current} / {pages}
            </span>
            <button
              type="button"
              onClick={() => setPage(current + 1)}
              disabled={current === pages}
              className="text-[12px] text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </span>
        </div>
      )}
    </div>
  )
}

/** 17c's assignment bar, which only exists once something is picked. */
export function BulkAssignBar({
  count,
  buckets,
  bucketId,
  onBucketChange,
  onAssign,
  onClear,
  isBusy,
}: {
  count: number
  buckets: { id: string; name: string }[]
  bucketId: string
  onBucketChange: (next: string) => void
  onAssign: () => void
  onClear: () => void
  isBusy: boolean
}) {
  if (count === 0) return null

  return (
    <div className="mt-3.5 flex flex-none flex-wrap items-center gap-4 rounded-md border bg-card px-3 py-2.5">
      <span className="text-[12px] font-medium">
        {count} selected
      </span>

      <span className="inline-flex items-baseline gap-2.5">
        <span className="text-[11.5px] text-muted-foreground">Assign to bucket</span>
        <span className="relative inline-flex items-baseline border-b pb-1 pr-4 text-[12px]">
          {buckets.find((bucket) => bucket.id === bucketId)?.name ?? "Pick one"}
          <select
            value={bucketId}
            onChange={(event) => onBucketChange(event.target.value)}
            aria-label="Assign to bucket"
            className="absolute inset-0 cursor-pointer opacity-0"
          >
            <option value="">Pick one</option>
            {buckets.map((bucket) => (
              <option key={bucket.id} value={bucket.id}>
                {bucket.name}
              </option>
            ))}
          </select>
        </span>
      </span>

      <button
        type="button"
        onClick={onAssign}
        disabled={!bucketId || isBusy}
        className="ml-auto inline-flex items-center gap-[7px] text-[12px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Check className="size-[13px]" />
        Assign
      </button>
      <button
        type="button"
        onClick={onClear}
        className="text-[12px] text-muted-foreground transition-colors hover:text-foreground"
      >
        Clear
      </button>
    </div>
  )
}
